import {
  useMemo,
  useState,
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Input, Label, Textarea, cn } from "@nexus/ui";
import { usePreflightDraftStore } from "../../stores/preflight-draft.store";
import { useUploadManager } from "../../hooks/useUploadManager";
import { parseUrlInput, type ParsedInvalidEntry } from "../../lib/parse-urls";
import type {
  CreateReportInput,
  PreflightMarketWithCount,
} from "../../types/preflight.types";
import { AssetList } from "../AssetList/AssetList";
import { MarketSelect } from "../MarketSelect/MarketSelect";
import { PlatformPlacementSelect } from "../PlatformPlacementSelect/PlatformPlacementSelect";
import { ScoringLogicInline } from "../ScoringLogicInline/ScoringLogicInline";
import { UploadDropzone } from "../UploadDropzone/UploadDropzone";
import { UrlInputFeedback } from "./UrlInputFeedback";
import { AnalyzeBlockersButton } from "./AnalyzeBlockersButton";

const EYEBROW_LABEL =
  "text-muted-foreground text-[11px] font-semibold tracking-wider uppercase";

const SCORING_PANEL_ID = "preflight-scoring-logic";

type UnmetReason = {
  id: "addAsset" | "platform" | "placement" | "market" | "brand" | "uploads";
  label: string;
};

function RequiredMark() {
  return (
    <span className="text-error font-normal" aria-hidden="true">
      *
    </span>
  );
}

export interface PreFlightHubInputCardProps {
  /** Wired by the Hub page to the create-report mutation (U5). */
  onAnalyze: (payload: CreateReportInput) => void;
  /** Dismisses the dialog; the form owns the button so it sits beside Analyze. */
  onCancel?: () => void;
  isSubmitting?: boolean;
  /** From tenant_settings.defaultBrand — sent as report brandName on analyze. */
  defaultBrand: string | null;
  isDefaultBrandLoading?: boolean;
  /** The tenant's markets; a report must be created in one of them (R12). */
  markets?: PreflightMarketWithCount[];
}

/**
 * The create form (Hub R1–R9 + KTD5, R12). Composes the dropzone, asset list,
 * URL paste, optional report name, the required market, and the dependent
 * platform/placement selects, all bound to the client-only draft store. It carries
 * no card framing of its own because it renders inside the create dialog (R11).
 * Brand comes from tenant settings.
 */
export function PreFlightHubInputCard({
  onAnalyze,
  onCancel,
  isSubmitting = false,
  defaultBrand,
  isDefaultBrandLoading = false,
  markets = [],
}: PreFlightHubInputCardProps) {
  const { t } = useTranslation();
  const draft = usePreflightDraftStore();
  const sessionEpoch = usePreflightDraftStore((s) => s.sessionEpoch);
  const manager = useUploadManager();
  const prevSessionEpochRef = useRef(sessionEpoch);
  useEffect(() => {
    if (prevSessionEpochRef.current !== sessionEpoch && sessionEpoch > 0) {
      manager.clearAll();
      setUrlInput("");
      setUrlSkipped(null);
    }
    prevSessionEpochRef.current = sessionEpoch;
  }, [sessionEpoch, manager]);

  // On mount: if the draft has confirmed uploads but the manager is empty (e.g. the
  // user navigated away and back), restore stub items so the asset list is visible and
  // the upload count shown to the user matches the validation count.
  useEffect(() => {
    const { uploadIds, uploadMetas } = usePreflightDraftStore.getState();
    if (uploadIds.length > 0 && manager.items.length === 0) {
      manager.hydrateFromStore(uploadIds, uploadMetas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs once on mount
  const [urlInput, setUrlInput] = useState("");
  const [isScoringOpen, setIsScoringOpen] = useState(false);
  const [urlSkipped, setUrlSkipped] = useState<{
    invalid: ParsedInvalidEntry[];
    unrecognized: string[];
    duplicates: number;
    capped: number;
  } | null>(null);

  const hasUrlInputError =
    (urlSkipped?.invalid.length ?? 0) > 0 ||
    (urlSkipped?.unrecognized.length ?? 0) > 0;

  /** Live preview of how many valid new URLs the current input would add. */
  const pendingUrlCount = useMemo(
    () =>
      parseUrlInput(urlInput, draft.urls, {
        existingAssetCount: draft.uploadIds.length + draft.urls.length,
      }).valid.length,
    [urlInput, draft.urls, draft.uploadIds.length],
  );

  function applyUrlParseResult(result: ReturnType<typeof draft.addUrlStrings>) {
    const hasFeedback =
      result.invalid.length > 0 ||
      result.unrecognized.length > 0 ||
      result.duplicates.length > 0 ||
      result.skipped.length > 0;

    setUrlSkipped(
      hasFeedback
        ? {
            invalid: result.invalid,
            unrecognized: result.unrecognized,
            duplicates: result.duplicates.length,
            capped: result.skipped.length,
          }
        : null,
    );

    const remaining = [...result.invalid.map((entry) => entry.raw), ...result.unrecognized];
    setUrlInput(remaining.join("\n"));
  }

  function commitUrlInput(raw = urlInput) {
    if (raw.trim().length === 0) return;
    applyUrlParseResult(draft.addUrlStrings(raw));
  }

  function handleUrlChange(value: string) {
    setUrlInput(value);
    // Editing the input is the user acting on the feedback — dismiss it.
    if (urlSkipped) setUrlSkipped(null);
  }

  function handleUrlKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      commitUrlInput();
    }
  }

  function handleUrlPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const text = event.clipboardData.getData("text");
    if (!text.trim()) return;
    event.preventDefault();
    if (urlSkipped) setUrlSkipped(null);
    const merged = urlInput.trim() ? `${urlInput}\n${text}` : text;
    commitUrlInput(merged);
  }

  const assetCount = draft.uploadIds.length + draft.urls.length;
  const hasPendingOrConfirmedAssets = assetCount > 0 || manager.items.length > 0;
  const brandName = defaultBrand?.trim() ?? "";

  const canAnalyze =
    assetCount > 0 &&
    brandName.length > 0 &&
    draft.platform !== null &&
    draft.placement !== null &&
    draft.marketIds.length > 0 &&
    !manager.hasInFlight &&
    !isSubmitting &&
    !isDefaultBrandLoading;

  // Brand is not editable from this card, so a missing tenant default is a dead end
  // unless we say so — the button would otherwise sit disabled with no explanation.
  const unmetReasons: UnmetReason[] = [];
  if (!hasPendingOrConfirmedAssets) {
    unmetReasons.push({
      id: "addAsset",
      label: t("preflight.inputCard.unmet.addAsset"),
    });
  }
  if (draft.platform === null) {
    unmetReasons.push({
      id: "platform",
      label: t("preflight.inputCard.unmet.platform"),
    });
  }
  if (draft.placement === null) {
    unmetReasons.push({
      id: "placement",
      label: t("preflight.inputCard.unmet.placement"),
    });
  }
  if (draft.marketIds.length === 0) {
    unmetReasons.push({
      id: "market",
      label: t("preflight.inputCard.unmet.market"),
    });
  }
  if (brandName.length === 0 && !isDefaultBrandLoading) {
    unmetReasons.push({
      id: "brand",
      label: t("preflight.inputCard.unmet.brand"),
    });
  }
  if (manager.hasInFlight) {
    unmetReasons.push({
      id: "uploads",
      label: t("preflight.inputCard.unmet.uploads"),
    });
  }

  function handleAnalyze() {
    if (
      !draft.platform ||
      !draft.placement ||
      draft.marketIds.length === 0 ||
      brandName.length === 0
    ) {
      return;
    }
    commitUrlInput();
    const state = usePreflightDraftStore.getState();
    onAnalyze({
      uploadIds: state.uploadIds,
      urls: state.urls,
      platform: draft.platform,
      placement: draft.placement,
      brandName,
      marketIds: draft.marketIds,
      name: state.name.trim() || undefined,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="preflight-name" className={EYEBROW_LABEL}>
              {t("preflight.inputCard.reportName")}{" "}
              <span className="font-normal normal-case">
                {t("preflight.inputCard.reportNameOptionalSuffix")}
              </span>
            </Label>
            <Input
              id="preflight-name"
              type="text"
              placeholder={t("preflight.inputCard.reportNamePlaceholder")}
              value={draft.name}
              onChange={(event) => draft.setName(event.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className={EYEBROW_LABEL}>
              {t("preflight.inputCard.market", "Market")} <RequiredMark />
            </Label>
            <MarketSelect
              mode="multi"
              markets={markets}
              value={draft.marketIds}
              onChange={draft.setMarketIds}
              disabled={isSubmitting}
              className="w-full max-w-none"
            />
          </div>
        </div>

        <PlatformPlacementSelect
          platform={draft.platform}
          placement={draft.placement}
          onPlatformChange={draft.setPlatform}
          onPlacementChange={draft.setPlacement}
          disabled={isSubmitting}
        />
      </section>

      <div className="border-border border-t" role="presentation" />

      <section className="flex flex-col gap-4">
        <Label className={EYEBROW_LABEL}>
          {t("preflight.inputCard.sections.creatives")} <RequiredMark />
        </Label>

        <UploadDropzone
          onFiles={manager.addFiles}
          disabled={isSubmitting}
          buttonLabel={
            assetCount > 0 ? t("preflight.dropzone.addMore") : undefined
          }
        />

        {manager.rejections.length > 0 ? (
          <ul
            className="flex flex-col gap-1"
            aria-label={t("preflight.inputCard.rejectedFiles")}
          >
            {manager.rejections.map((rejection, index) => (
              <li
                key={`${rejection.fileName}-${index}`}
                className="text-error flex items-center gap-1.5 text-xs"
              >
                <Icon name="alert-circle" size="xs" />
                <span>
                  {`${rejection.fileName} — ${rejection.reason
                    .replace(/_/g, " ")
                    .toLowerCase()}`}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="preflight-url" className={EYEBROW_LABEL}>
            {t("preflight.inputCard.assetUrls")}
          </Label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="relative min-w-0 flex-1">
              <Icon
                name="link"
                size="xs"
                className="text-muted-foreground pointer-events-none absolute top-2.5 left-3"
              />
              <Textarea
                id="preflight-url"
                placeholder={t("preflight.inputCard.urlPlaceholder")}
                value={urlInput}
                onChange={(event) => handleUrlChange(event.target.value)}
                onBlur={() => commitUrlInput()}
                onPaste={handleUrlPaste}
                onKeyDown={handleUrlKeyDown}
                disabled={isSubmitting}
                aria-invalid={hasUrlInputError || undefined}
                className={cn(
                  "min-h-[68px] pl-8",
                  hasUrlInputError && "border-error focus-visible:border-error focus-visible:ring-error/20",
                )}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              className="w-full shrink-0 sm:w-auto"
              onClick={() => commitUrlInput()}
              disabled={isSubmitting || urlInput.trim().length === 0}
            >
              {pendingUrlCount > 0
                ? t("preflight.inputCard.addUrlCount", {
                    count: pendingUrlCount,
                  })
                : t("preflight.inputCard.addUrl")}
            </Button>
          </div>
          {urlSkipped ? (
            <UrlInputFeedback
              invalid={urlSkipped.invalid}
              unrecognized={urlSkipped.unrecognized}
              duplicates={urlSkipped.duplicates}
              capped={urlSkipped.capped}
            />
          ) : null}
          <p className="text-muted-foreground text-xs">
            {t("preflight.inputCard.urlHelp")}
          </p>
        </div>

        <AssetList
          items={manager.items}
          urls={draft.urls}
          onCancel={manager.cancel}
          onRetry={manager.retry}
          onRemoveItem={manager.removeItem}
          onRemoveUrl={draft.removeUrl}
        />
      </section>

      <div className="border-border flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="link"
          size="sm"
          iconStart="sliders-horizontal"
          aria-expanded={isScoringOpen}
          aria-controls={isScoringOpen ? SCORING_PANEL_ID : undefined}
          onClick={() => setIsScoringOpen((open) => !open)}
        >
          {t("preflight.inputCard.howScoringWorks")}
          <Icon
            name="chevron-down"
            size="xs"
            className={cn(
              "transition-transform duration-200 motion-reduce:transition-none",
              isScoringOpen && "rotate-180",
            )}
          />
        </Button>
        <div className="flex items-center gap-2 sm:justify-end">
          {onCancel ? (
            <Button type="button" variant="secondary" onClick={onCancel}>
              {t("common.cancel")}
            </Button>
          ) : null}
          <AnalyzeBlockersButton
            canAnalyze={canAnalyze}
            isSubmitting={isSubmitting}
            blockers={unmetReasons}
            onAnalyze={handleAnalyze}
          />
        </div>
      </div>

      {isScoringOpen ? (
        <ScoringLogicInline
          id={SCORING_PANEL_ID}
          platform={draft.platform ?? "META"}
        />
      ) : null}
    </div>
  );
}
